import React, { useEffect, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { apiGet, apiPatch } from '../../lib/api'
import { UpdateOrderStatusInput } from '@buddys-choice/shared'
import type { Order, UpdateOrderStatusInput as UpdateOrderStatusInputType } from '@buddys-choice/shared'
import { SearchableSelect } from '../../components/SearchableSelect'

const ORDER_STATUS_OPTIONS = [
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'shipped', label: 'Shipped' },
]

export const AdminOrderStatusPage: React.FC = () => {
  const { orderId } = useParams({ from: '/admin/orders/$orderId/status' })
  const navigate = useNavigate()
  const [order, setOrder] = useState<Pick<Order, 'id' | 'orderNumber' | 'status' | 'customerName'> | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [submitError, setSubmitError] = useState('')

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<UpdateOrderStatusInputType>({
    resolver: zodResolver(UpdateOrderStatusInput),
    defaultValues: { status: 'confirmed', message: '' },
  })

  useEffect(() => {
    apiGet<Order>(`orders/${orderId}`)
      .then((data) => {
        setOrder(data)
        setValue('status', data.status)
        setValue(
          'message',
          data.status === 'pending'
            ? 'Order confirmed by store manager.'
            : data.status === 'confirmed'
              ? 'Order sent to workshop for quality check.'
              : data.status === 'processing'
                ? 'Shipped via courier with tracking update.'
                : 'Order delivered successfully to customer.'
        )
      })
      .catch((err: Error) => setLoadError(err.message || 'Failed to load order.'))
      .finally(() => setLoading(false))
  }, [orderId, setValue])

  const handleUpdateStatus = async (data: UpdateOrderStatusInputType) => {
    setSubmitError('')
    try {
      await apiPatch(`orders/${orderId}/status`, data)
      navigate({ to: '/admin/orders/$orderId', params: { orderId } })
    } catch (err: unknown) {
      setSubmitError((err as Error).message || 'Failed to update order status.')
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-8 h-8 border-2 border-[var(--gold-main)] border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (loadError || !order) {
    return (
      <div className="space-y-4">
        <Link to="/admin/orders" className="inline-flex items-center gap-1.5 text-xs text-[var(--text-muted)] hover:text-[var(--gold-main)]">
          <ArrowLeft className="w-4 h-4" /> Back to orders
        </Link>
        <div className="glass-luxury-card p-4 text-sm text-red-400">{loadError || 'Order not found.'}</div>
      </div>
    )
  }

  return (
    <div className="space-y-5 sm:space-y-6 max-w-lg">
      <Link
        to="/admin/orders/$orderId"
        params={{ orderId }}
        className="inline-flex items-center gap-1.5 text-xs text-[var(--text-muted)] hover:text-[var(--gold-main)] min-h-[44px]"
      >
        <ArrowLeft className="w-4 h-4" /> Back to order
      </Link>

      <div className="glass-luxury-card p-4 sm:p-6">
        <h1 className="text-lg sm:text-xl font-title font-bold mb-1">Update order status</h1>
        <p className="text-xs text-[var(--text-muted)] mb-6">
          {order.orderNumber} · {order.customerName}
        </p>

        {submitError && <div className="alert alert-error text-xs mb-4">{submitError}</div>}

        <form onSubmit={handleSubmit(handleUpdateStatus)} className="space-y-4 text-sm">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[var(--text-primary)] mb-2">
              New status *
            </label>
            <Controller
              control={control}
              name="status"
              render={({ field }) => (
                <SearchableSelect
                  value={field.value}
                  onChange={field.onChange}
                  options={ORDER_STATUS_OPTIONS}
                  placeholder="Select status"
                  aria-label="New order status"
                  className="w-full"
                />
              )}
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[var(--text-primary)] mb-2">
              Tracking message / note for customer *
            </label>
            <textarea
              {...register('message')}
              rows={4}
              placeholder="e.g. Shipped via DTDC Express with tracking AWB991823"
              className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-3 px-4 text-xs outline-none resize-none"
              aria-invalid={!!errors.message}
            />
            {errors.message && <span className="text-xs text-red-400 mt-1 block">{errors.message.message}</span>}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Link
              to="/admin/orders/$orderId"
              params={{ orderId }}
              className="btn-gold-outline flex-1 text-center text-xs py-3 min-h-[44px] flex items-center justify-center"
            >
              Cancel
            </Link>
            <button type="submit" disabled={isSubmitting} className="btn-gold flex-1 text-xs py-3 font-semibold min-h-[44px]">
              {isSubmitting ? (
                <div className="w-4 h-4 border-2 border-slate-950 border-t-transparent rounded-full animate-spin mx-auto" />
              ) : (
                'Update status'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
